import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { NativeF0Frame } from '@muzly/shared';

import { framesToClip } from '../capture';
import { useOnboarding } from '../context';
import { CaptureProbe, isProbeOn } from '../probe';
import {
  RECORD_TESTS,
  SEGMENT_PROMPTS,
  attemptWarning,
  describeAttempt,
  isLastSegment,
  isLastTest,
  nextPosition,
  segmentAt,
  takeSpanSec,
} from '../recordTests';
import { isRoomTooNoisy } from '../roomNoise';
import { SUSTAIN_TONE_URL } from '../sustainGuide';
import { useRecordMachine } from '../useRecordMachine';
import { AllDoneView } from './AllDoneView';
import { CloseDiagnosisButton } from './CloseDiagnosisButton';
import { CountdownOverlay } from './CountdownOverlay';
import { NoisyRoomView } from './NoisyRoomView';
import { RecordReviewView } from './RecordReviewView';
import { RecordTestView } from './RecordTestView';
import { SongReferenceAudio } from './SongReferenceAudio';
import { SongReferenceGate } from './SongReferenceGate';
import { StepDoneView } from './StepDoneView';
import { TestGuideCard } from './TestGuideCard';
import { useSongReference } from './useSongReference';

import type { CaptureSource, SegmentId } from '../capture';
import type { MelodyNote } from '../melody';
import type { RecordPosition } from '../recordTests';
import type { SongScore } from '../songScoring';
import type { RecordErrKind, RecordMachine } from '../useRecordMachine';

/**
 * 진단 녹음 화면 — 상태기계(useRecordMachine)가 고른 단계를 그린다.
 *
 * 6단계: 가이드 → 3-2-1 → 측정 → **확인**(방금 잰 값 + 재진단) → … → 완료 / 에러.
 * 확인 단계가 재시도를 **세그먼트 단위**로 만든다 — 마지막에 몰아서 판정하면 무효가 났을
 * 때 전부 다시 찍어야 한다(티켓 12). 자체 전이를 가지므로 StepShell 기본 CTA는 숨긴다.
 *
 * 캡처는 네이티브 PitchTracker 엔진 경유 — 마이크는 네이티브가 소유하고 JS로는 f0/rms만
 * 건너온다. 스펙트럼이 없어 리포트의 resonance·singer's formant 축은 비활성.
 */

const errorKey = (err: RecordErrKind, source: CaptureSource | null): string =>
  // 브라우저 개발/데모 경로는 마이크 대신 합성 멜로디를 분석한다. 그 멜로디는 지속음
  // 유효성(`isValidVoice`)을 통과하지 못해 **항상 여기서 막히는데**, 기본 문구는 사용자
  // 목소리를 탓한다. 다시 해도 같은 결과라 무엇이 벽인지 말해 주는 게 유일하게 정직하다.
  err === 'invalid' && source === 'mock'
    ? 'web.diagnosis.error.mockInvalid'
    : `web.diagnosis.error.${err}`;

interface AttemptReview {
  summary: string;
  warning: string | null;
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** 방금 끝난 시도의 요약 + 경고 — 다시 할지 판단할 근거. 노래는 songReviewOf가 맡는다. */
const reviewOf = (
  segment: Exclude<SegmentId, 'song'>,
  tries: NativeF0Frame[][],
  t: Translate,
): AttemptReview => {
  const clip = framesToClip(tries[tries.length - 1] ?? []);
  const warning = attemptWarning(segment, clip);
  const summary = describeAttempt(segment, clip);
  return {
    summary: t(summary.key, summary.params),
    warning: warning && t(warning.key, warning.params),
  };
};

/** 더 크게 치우친 구간 한 줄 — 없으면 말하지 않는다(치우침 없음도 사실이지만 경고는 아니다). */
const tendencyNote = (score: SongScore, t: Translate): string | null => {
  const segments = [
    { id: 'high', seg: score.high },
    { id: 'low', seg: score.low },
  ]
    .filter(({ seg }) => seg.tendency != null && seg.tendency !== 'onPitch')
    .sort(
      (a, b) => Math.abs(b.seg.centDev ?? 0) - Math.abs(a.seg.centDev ?? 0),
    );
  const worst = segments[0];
  return worst
    ? t(`web.diagnosis.song.tendency.${worst.id}.${worst.seg.tendency}`)
    : null;
};

/**
 * 노래 시도의 요약 — **레퍼런스 대비 채점**이다(clip만 보는 describeAttempt로는 못 낸다).
 * 채점은 프레임을 쥔 상태기계가 하고(`useRecordMachine`), 여기는 그 값을 문장으로 옮긴다.
 *
 * 채점이 안 되면 **0%가 아니라 "측정 불가"**라고 말한다 — 소리를 안 냈는데 0점이 뜨면
 * 사용자는 자기가 틀렸다고 읽는다.
 */
const songReviewOf = (
  score: SongScore | undefined,
  t: Translate,
): AttemptReview => {
  if (!score) {
    return { summary: t('web.diagnosis.song.unmeasured'), warning: null };
  }
  return {
    summary: t('web.diagnosis.song.result', {
      accuracy: Math.round(score.accuracy),
      sung: score.sungNotes,
      total: score.totalNotes,
    }),
    warning: tendencyNote(score, t),
  };
};

/**
 * 확인 다이얼로그가 뭐라고 말할지. 진단이 끝났으면 완료를 알리고, 같은 진단 안에 세그먼트가
 * 남았으면(저음 → 고음) 다음에 낼 소리를 미리 말한다 — 여기서 "완료"라고 하면 고음을 왜
 * 또 재는지 알 수 없다.
 */
const reviewCopy = (
  pos: RecordPosition,
  testTitle: string,
  t: Translate,
): { title: string; hint: string | null } => {
  if (!isLastSegment(pos)) {
    const next = nextPosition(pos) as RecordPosition;
    return { title: t(SEGMENT_PROMPTS[segmentAt(next)].upNextKey), hint: null };
  }
  return {
    title: t('web.diagnosis.review.testDone', { test: testTitle }),
    hint: t(
      isLastTest(pos)
        ? 'web.diagnosis.review.lastTestHint'
        : 'web.diagnosis.review.nextTestHint',
    ),
  };
};

const ErrorScreen = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6">
      <p
        role="alert"
        aria-live="assertive"
        className="text-center text-sm text-muted-foreground"
      >
        {message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="min-h-[44px] rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground"
      >
        {t('web.diagnosis.error.retry')}
      </button>
    </div>
  );
};

/** 측정 화면 + 그 위에 뜨는 것들(카운트다운·확인 다이얼로그). */
const MeasureScreen = ({
  m,
  guide,
  guideAudioDurationMs,
}: {
  m: RecordMachine;
  /** 노래 진단의 정답 멜로디 — 측정 화면이 곡선 아래에 깔아 준다. */
  guide?: MelodyNote[];
  /** 그 곡 음원의 길이(ms) — 축과 제한시간이 음원을 끝까지 덮게 한다. */
  guideAudioDurationMs?: number;
}) => {
  const { t } = useTranslation();
  // 스위치는 URL이 정하고 URL이 바뀌려면 새로 뜬다 — 프레임마다(~30fps) 저장소를 다시
  // 읽을 이유가 없다.
  const probeOn = useMemo(() => isProbeOn(), []);
  const inReview = m.phase === 'review';
  /**
   * 방이 시끄러워 측정이 성립하지 않는다(시안 `884:9858`). 측정 중에만 본다 —
   * 확인 화면까지 이 판정을 들고 가면 이미 잰 값을 놓고 방 이야기를 하게 된다.
   */
  const noisyRoom =
    m.phase === 'recording' &&
    !m.paused &&
    isRoomTooNoisy({
      floorLocked: m.live.floorLocked,
      voicedSec: m.live.voicedSec,
      elapsedSec: m.elapsedSec,
      // 노래 진단은 반주가 스피커로 나가므로 조용한 창이 없는 것이 정상이다.
      backingTrack: m.test.id === 'song',
    });

  // 화면 전환이지 덮개가 아니다 — 뒤에서 타이머와 그래프가 계속 돌면 "저 값은 살아
  // 있나"가 화면에 남는다. 다시 재는 것은 이번 세그먼트만이다(진단 전체가 아니라).
  if (noisyRoom) {
    return <NoisyRoomView onRetry={m.retrySegment} />;
  }

  // 진단 하나를 마쳤으면 화면을 갈아탄다(시안 `884:9451`). 같은 진단 안의 세그먼트
  // 이동(저음 → 고음)은 아래의 작은 다이얼로그로 남는다 — 화면을 갈아탈 일이 아니다.
  if (inReview && isLastSegment(m.pos)) {
    const summary =
      m.segment === 'song'
        ? songReviewOf(m.songScore, t)
        : reviewOf(m.segment, m.tries, t);
    return (
      <StepDoneView
        step={m.pos.testIndex + 1}
        testTitle={t(m.test.titleKey)}
        hint={t(
          isLastTest(m.pos)
            ? 'web.diagnosis.review.lastTestHint'
            : 'web.diagnosis.review.nextTestHint',
        )}
        summary={summary.summary}
        warning={summary.warning}
        attemptCount={m.tries.length}
        onRetry={m.retrySegment}
        onNext={m.nextSegment}
        busy={m.finishing}
      />
    );
  }

  return (
    <div className="relative flex flex-1 flex-col">
      {/* 계측 오버레이 — `?probe=1` 로만 뜬다(`probe/probeFlag.ts`). */}
      {probeOn && <CaptureProbe source={m.source} live={m.live} />}
      {/* 다이얼로그가 떠 있는 동안 뒤 화면은 포커스도 낭독도 받지 않는다 — 방금 누른
          완료 버튼이 가려진 채 포커스를 쥐고 있으면 키보드가 갇힌다. */}
      {/* `false`가 아니라 undefined를 넘긴다 — inert는 값이 아니라 **있고 없고**로 도는
          속성이라 `inert=""`가 남으면 녹음 내내 완료 버튼을 못 누른다. */}
      <div inert={inReview || undefined} className="flex flex-1 flex-col">
        <RecordTestView
          test={m.test}
          segment={m.segment}
          live={m.live}
          elapsedSec={m.elapsedSec}
          isRecording={m.phase === 'recording'}
          stepIndex={m.pos.testIndex}
          stepTotal={RECORD_TESTS.length}
          onFinish={m.stop}
          paused={m.paused}
          onPause={m.pause}
          onResume={m.resume}
          guide={guide}
          guideAudioDurationMs={guideAudioDurationMs}
        />
      </div>

      {m.phase === 'countdown' && <CountdownOverlay count={m.count} />}
      {inReview && !isLastSegment(m.pos) && (
        <RecordReviewView
          {...reviewCopy(m.pos, t(m.test.titleKey), t)}
          {...(m.segment === 'song'
            ? songReviewOf(m.songScore, t)
            : reviewOf(m.segment, m.tries, t))}
          attemptCount={m.tries.length}
          onRetry={m.retrySegment}
          onConfirm={m.nextSegment}
        />
      )}
    </div>
  );
};

/**
 * 셸이 `bare`라 여백이 없다 — 측정 화면 **말고** 이 단계의 다른 화면들(가이드·음원 게이트·
 * 에러·완료)은 여백 안에 놓여야 하므로 여기서 감싼다. 측정 화면만 가장자리까지 찬다.
 */
const Padded = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-1 flex-col px-5 pb-8">{children}</div>
);

export const VoiceRecordStep = () => {
  const { t } = useTranslation();
  const { next } = useOnboarding();
  // 레퍼런스는 진단 화면에 들어서자마자 받는다 — 노래는 마지막 진단이라 앞의 두 진단
  // 동안 받아 두면 대기가 안 보이고, 카운트다운이 끝난 뒤 재생이 실패하는 일도 없다.
  // 멜로디는 상태기계로 내려간다: 채점은 프레임을 쥔 그쪽에서 돈다.
  const song = useSongReference();
  const m = useRecordMachine(
    song.reference?.melody,
    song.reference?.durationMs,
  );
  const isSong = m.test.id === 'song';
  // 가이드는 노래 진단에만 내려간다 — 다른 진단엔 따라 부를 정답이 없다. 안내 카드가
  // 약속하는 시간과 측정 화면의 축이 같은 값을 쓰려면 둘 다 이것을 봐야 한다.
  const guide = isSong ? song.reference?.melody : undefined;
  // 음원 길이는 차트와 **같이** 흘러야 한다 — 하나만 넘기면 테이크가 다시 차트 끝에서
  // 끊기고 그 뒤 반주 아웃트로가 잘린다.
  const guideAudioDurationMs = isSong ? song.reference?.durationMs : undefined;
  if (m.phase === 'error') {
    return (
      <Padded>
        {/* 다른 화면들과 같은 자리에 X를 남긴다 — 마이크 권한이 거부돼도 여기서
            막다른 길이 되면 안 된다(진단 플로우는 늘 closable). */}
        <header className="flex items-center justify-end">
          <CloseDiagnosisButton />
        </header>
        <ErrorScreen
          message={m.err ? t(errorKey(m.err, m.source)) : ''}
          onRetry={m.restartAll}
        />
      </Padded>
    );
  }

  if (m.phase === 'done') {
    return (
      <Padded>
        <AllDoneView onResult={next} onRetry={m.restartAll} />
      </Padded>
    );
  }

  const guideCard = (
    <TestGuideCard
      test={m.test}
      index={m.pos.testIndex}
      total={RECORD_TESTS.length}
      onStart={m.begin}
      limitSec={takeSpanSec(m.test, guide, guideAudioDurationMs)}
      // 미리듣기 두 갈래. 노래는 **가이드 보컬 버전**을(부를 멜로디가 들려야 한다),
      // 지속음은 배포된 기준음 파일을 바로 건다 — 1.3초짜리라 미리 받아 둘 이유가 없고,
      // 못 받으면 재생만 조용히 실패한다(진단은 그대로 간다). 둘 다 녹음 전이라 채점의
      // 시간 앵커와 무관하다.
      {...(isSong && song.reference
        ? { previewUrl: song.reference.guideAudioObjectUrl }
        : m.test.id === 'sustain'
          ? { previewUrl: SUSTAIN_TONE_URL }
          : {})}
    />
  );

  if (m.phase === 'guide') {
    return (
      <Padded>
        {isSong ? (
          <SongReferenceGate
            status={song.status}
            onRetry={song.retry}
            onSkip={m.skipTest}
          >
            {guideCard}
          </SongReferenceGate>
        ) : (
          guideCard
        )}
      </Padded>
    );
  }

  return (
    <>
      {/* 가이드는 노래 진단에만 내려간다 — 다른 진단엔 따라 부를 정답이 없다. */}
      <MeasureScreen
        m={m}
        guide={guide}
        guideAudioDurationMs={guideAudioDurationMs}
      />
      {/* 재생은 녹음 단계에 들어서는 순간 건다. 게이트의 방 재기(`voice-gate.ts`
          CALIBRATION_MS)는 카운트다운 동안 이미 끝났다 — 세션을 카운트다운에서 여는 이유가
          그것이다(`useRecordMachine`). 반주가 그 창 안에서 울리면 잰 것이 방이 아니라
          반주라 바닥을 영영 못 잠갔고(`floor=OPEN`, `oct=114`), 잠글 때까지 미루면 첫
          프레임부터 부르는 사람에게는 반주가 중간에 나왔다(2026-09-16 실기기 둘 다).
          채점 앵커는 첫 프레임인데 프레임도 이 순간부터 모으므로(`markTake`) 따로 안 넘긴다.
          여기는 **반주**다: 가이드 보컬을 틀면 마이크가 그걸 같이 듣고 채점한다. */}
      {isSong && song.reference && (
        <SongReferenceAudio
          src={song.reference.audioObjectUrl}
          playing={m.phase === 'recording'}
        />
      )}
    </>
  );
};
